import React, { useEffect, useState } from "react";
import { Button, Input, Switch, Text } from "@fluentui/react-components";
import {
  Add20Filled,
  Delete20Regular,
  ChevronDown20Regular,
  ChevronUp20Regular,
} from "@fluentui/react-icons";
import QuestionComponent from "./QuestionComponent";
import GenerateAudioContainer from "../../../Containers/GenericContainer/GenerateAudioContainer";
import { ConfirmAlert } from "../../GenericComponent/ConfirmAlert";
import { useTranslation } from "react-i18next";
import "../../CSS/SurveyComponent.css";

export default function SurveyComponent({ callFlowData, setCallFlowData, audioList, permissions, isEditMode, ACTIVE_TENANT }) {
  const { t } = useTranslation();
  const [surveyEnabled, setSurveyEnabled] = useState(callFlowData.survey?.enabled || false);
  const [showSurveyDetails, setShowSurveyDetails] = useState(surveyEnabled);
  const [showQuestionInput, setShowQuestionInput] = useState(false);
  const [newQuestionName, setNewQuestionName] = useState("");
  const [selectedQuestionToDelete, setSelectedQuestionToDelete] = useState(null);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [addQuestionError, setAddQuestionError] = useState(null);

  const survey = callFlowData.survey || { questions: {}, goodByeMessage: {}, timeout: 0 };
  const goodbyeQuestionName = "goodByeMessage";

  useEffect(() => {
    setSurveyEnabled(callFlowData.survey?.enabled || false);
    setShowSurveyDetails(callFlowData.survey?.enabled || false);
  }, [callFlowData.survey]);

  const toggleSurveyEnabled = (checked) => {
    setSurveyEnabled(checked);
    setShowSurveyDetails(checked);
    setCallFlowData((prev) => ({
      ...prev,
      survey: { ...prev.survey, enabled: checked },
    }));
  };

  const toggleQuestionDetails = (questionName) => {
    setExpandedQuestion((prev) => (prev === questionName ? null : questionName));
  };

  const addQuestion = () => {
    if (!newQuestionName || newQuestionName.includes(" ")) {
      setAddQuestionError(t("invalidQuestionName"));
      return;
    }

    const defaultQuestion = {
      type: "",
      questionText: "",
      Order: "",
      audio: { type: "text2speech", language: callFlowData.language },
    };

    setCallFlowData((prev) => ({
      ...prev,
      survey: {
        ...prev.survey,
        questions: {
          ...prev.survey.questions,
          [newQuestionName]: defaultQuestion,
        },
      },
    }));

    setShowQuestionInput(false);
    setNewQuestionName("");
    setAddQuestionError(null);
  };

  const confirmRemoveQuestion = (questionName) => {
    setSelectedQuestionToDelete(questionName);
  };

  const removeQuestion = () => {
    if (!selectedQuestionToDelete) return;
    const newQuestions = { ...survey.questions };
    delete newQuestions[selectedQuestionToDelete];

    setCallFlowData((prev) => ({
      ...prev,
      survey: { ...prev.survey, questions: newQuestions },
    }));
    setSelectedQuestionToDelete(null);
  };

  const handleTimeoutChange = (value) => {
    if (isNaN(value)) return;
    setCallFlowData((prev) => ({
      ...prev,
      survey: { ...prev.survey, timeout: value },
    }));
  };

  const setGoodbyeAudio = (audio) => {
    setCallFlowData((prev) => ({
      ...prev,
      survey: { ...prev.survey, goodByeMessage: audio },
    }));
  };

  return (
    <div className="survey-container">
      {/* Survey activation toggle */}
      <div className="survey-toggle">
        <Text weight="semibold">{t("activateSurvey")}</Text>
        <Switch 
          checked={surveyEnabled} 
          disabled={isEditMode ? !permissions?.survey?.canUpdate : !permissions?.survey?.canCreate}
          onChange={(e, data) => toggleSurveyEnabled(data.checked)} 
        />
      </div>

      {showSurveyDetails && (
        <>
          {/* Timeout */}
          <div className="survey-timeout">
            <Text weight="semibold">{t("timeout")}</Text>
            <div className="survey-timeout-input">
              <Input
                type="number"
                min={0}
                value={survey.timeout || 0}
                disabled={isEditMode ? !permissions?.survey?.canUpdate : !permissions?.survey?.canCreate}
                onChange={(e, data) => handleTimeoutChange(data.value)}
              />
              <Text>{t("seconds")}</Text>
            </div>
          </div>

          {/* Questions */}
          <div className="survey-questions-list">
            {Object.keys(survey.questions || {}).map((questionName) => (
              <div key={questionName} className="survey-question">
                <div className="survey-question-header">
                  <div className="survey-question-title">
                    <Button
                      icon={
                        expandedQuestion === questionName ? (
                          <ChevronUp20Regular />
                        ) : (
                          <ChevronDown20Regular />
                        )
                      }
                      appearance="subtle"
                      onClick={() => toggleQuestionDetails(questionName)}
                    />
                    <Text>{t(questionName) || questionName}</Text>
                  </div>
                  <Button
                    icon={<Delete20Regular />}
                    appearance="subtle"
                    disabled={!permissions?.survey?.canDelete}
                    onClick={() => confirmRemoveQuestion(questionName)}
                  />
                </div>

                {expandedQuestion === questionName && (
                  <div className="survey-question-details">
                    <QuestionComponent
                      audioList={audioList}
                      questionName={questionName}
                      setCallCenterData={setCallFlowData}
                      question={survey.questions[questionName]}
                      callFlowId={callFlowData.id}
                      callFlowLanguage={callFlowData.language}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add new question */}
          {showQuestionInput ? (
            <div className="survey-add-question">
              <Input
                className="survey-input"
                placeholder={t("questionNamePlaceholder")}
                value={newQuestionName}
                disabled={isEditMode ? !permissions?.survey?.canUpdate : !permissions?.survey?.canCreate}
                onChange={(e, data) => setNewQuestionName(data.value)}
              />
              <Button 
                icon={<Add20Filled />} 
                disabled={isEditMode ? !permissions?.survey?.canUpdate : !permissions?.survey?.canCreate}
                onClick={addQuestion} 
              />
              {addQuestionError && <Text className="error-message">{addQuestionError}</Text>}
            </div>
          ) : (
            <Button
              appearance="primary"
              icon={<Add20Filled />}
              disabled={isEditMode ? !permissions?.survey?.canUpdate : !permissions?.survey?.canCreate}
              onClick={() => setShowQuestionInput(true)}
              className="add-question-btn"
            >
            </Button>
          )}

          {/* Goodbye message */}
          <div className="survey-goodbye">
            <Text weight="semibold">{t("goodbyeMessage")}</Text>
            <div className="survey-goodbye-audio">
              <GenerateAudioContainer
                /* La langue CHOISIE, pas celle du flux.
                   Le rappel ignorait son argument et réécrivait `callFlowData.language` : sélectionner
                   le français dans le générateur d'audio n'avait donc aucun effet, la valeur
                   retombait sur la langue du flux à chaque enregistrement. Tous les autres
                   appelants de ce composant relaient bien la langue — les deux blocs de
                   l'enquête étaient les seuls à la perdre.

                   La conséquence dépassait l'affichage : `Survey.js` passe cette langue à la
                   reconnaissance vocale d'ACS. Une réponse française reconnue par un modèle
                   anglais ne lève aucune erreur, elle rend un texte faux. */
                setLangue={(lang) =>
                  setGoodbyeAudio({
                    ...survey.goodByeMessage,
                    type: "text2speech",
                    language: lang || callFlowData.language,
                  })
                }
                setDelete={() =>
                  setGoodbyeAudio({
                    type: "text2speech",
                    audioFileUrl: "",
                    text: "",
                    selectedMedia: null,
                  })
                }
                saveAudio={(url) =>
                  setGoodbyeAudio({
                    ...survey.goodByeMessage,
                    audioFileUrl: url,
                  })
                }
                setText={(text) =>
                  setGoodbyeAudio({
                    ...survey.goodByeMessage,
                    text,
                    type: "text2speech",
                  })
                }
                setAudio={(selectedMedia) =>
                  setGoodbyeAudio({
                    ...survey.goodByeMessage,
                    selectedMedia,
                    audioFileUrl: selectedMedia?.url || "",
                    text: "",
                    type: "Audiofile",
                  })
                }
                setType={(type) =>
                  setGoodbyeAudio({
                    ...survey.goodByeMessage,
                    type,
                  })
                }
                audioFileUrl={survey.goodByeMessage?.audioFileUrl || ""}
                fileName={survey.goodByeMessage?.selectedMedia?.header}
                type={survey.goodByeMessage?.type || "text2speech"}
                text={survey.goodByeMessage?.text || ""}
                path={`survey/${goodbyeQuestionName}`}
                defaultLanguage={
                  survey.goodByeMessage?.language || callFlowData.language
                }
                disabled={isEditMode ? !permissions?.survey?.canUpdate : !permissions?.survey?.canCreate}
                canPlayAudio={permissions?.survey?.canRead}
              />
            </div>
          </div>
        </>
      )}

      {selectedQuestionToDelete && (
        <ConfirmAlert
          open={!!selectedQuestionToDelete}
          name={selectedQuestionToDelete}
          onConfirm={removeQuestion}
          onCancel={() => setSelectedQuestionToDelete(null)}
        />
      )}
    </div>
  );
}
